import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-add.svg"
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as DownloadIcon } from "../assets/images/icons/ic-download.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-edit.svg"
import { ReactComponent as EmptyMaterials } from "../assets/images/illustrations/3D/empty-materials.svg"
import { Each } from "../common/Each"
import Button from "../components/Button"
import Card from "../components/cards/Card"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import EmptyContent from "../components/EmptyContent"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import Loader from "../components/Loader"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Select from "../components/Select"
import Skeleton from "../components/Skeleton"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import Uploader from "../components/Uploader"
import { DialogStatus } from "../enums"
import typo from "../typography.module.css"
import { formatDateV2, formatFileSize, formatTimeV2 } from "../utils"
import styles from "./Materials.module.css"

const mergeCollapsedState = (previousState, nextIds, defaultCollapsed = true) => {
  const current = new Map(previousState.map((item) => [item.id, item.collapsed]))
  return nextIds.map((id) => ({
    id,
    collapsed: current.has(id) ? current.get(id) : defaultCollapsed,
  }))
}

const groupMaterialsByCourseEdition = (items = [], t) => {
  const grouped = items.reduce((acc, material) => {
    const course = material?.module?.course
    const edition = material?.module?.edition

    const courseId = course?.id ?? "no-course"
    if (!acc[courseId]) {
      acc[courseId] = {
        course: {
          id: courseId,
          name: course?.name ?? t("materials.title"),
          thumbnail: course?.thumbnail ?? null,
        },
        editionsMap: {},
      }
    }

    const editionId = edition?.id ?? `no-edition-${courseId}`
    if (!acc[courseId].editionsMap[editionId]) {
      acc[courseId].editionsMap[editionId] = {
        edition: {
          id: editionId,
          name: edition?.name ?? "Senza edizione",
        },
        materials: [],
      }
    }

    acc[courseId].editionsMap[editionId].materials.push(material)
    return acc
  }, {})

  return Object.values(grouped)
    .map((courseGroup) => ({
      ...courseGroup,
      editions: Object.values(courseGroup.editionsMap)
        .map((editionGroup) => ({
          ...editionGroup,
          materials: [...editionGroup.materials].sort((a, b) => {
            const ta = a?.created_at ? new Date(a.created_at).getTime() : 0
            const tb = b?.created_at ? new Date(b.created_at).getTime() : 0
            return tb - ta
          }),
        }))
        .sort((a, b) => (a.edition?.name ?? "").localeCompare(b.edition?.name ?? "", undefined, { sensitivity: "base" })),
    }))
    .sort((a, b) => (a.course?.name ?? "").localeCompare(b.course?.name ?? "", undefined, { sensitivity: "base" }))
}

const mapGroupsByCourseId = (groups = []) => {
  const map = {}
  for (const group of groups) {
    map[group?.course?.id] = group
  }
  return map
}

const getMaterialFileType = (material) => {
  const source = material?.name || material?.url || ""
  const extension = source.includes(".") ? source.split(".").pop().toLowerCase() : ""

  if (!extension) return "-"

  const groupedTypes = {
    Immagine: ["jpg", "jpeg", "png", "svg", "webp", "gif", "avif", "apng", "heic"],
    Video: ["mp4", "m4p", "mov", "flv", "mkv", "avi", "wmv", "mpg", "mpeg", "m2v"],
    PDF: ["pdf"],
    Word: ["doc", "docx"],
    Excel: ["xls", "xlsx"],
    PowerPoint: ["ppt", "pptx"],
    Audio: ["mp3", "wav", "aac", "ogg", "m4a"],
    Archivio: ["zip", "rar", "7z", "tar", "gz"],
  }

  for (const [label, extensions] of Object.entries(groupedTypes)) {
    if (extensions.includes(extension)) {
      return label
    }
  }

  return extension.toUpperCase()
}

const normalizeSearchText = (value = "") => `${value}`.toLowerCase().trim().replaceAll(" ", "")

const filterSelectOptions = (options = [], query = "") => {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) return options

  return options.filter((item) => normalizeSearchText(item?.name).includes(normalizedQuery))
}

const isLiveLesson = (lesson) => {
  const kind = `${lesson?.type ?? lesson?.status ?? lesson?.state ?? ""}`.toLowerCase()
  return kind === "live"
}

const getLessonSelectName = (lesson, fallbackName = "") => {
  const baseName = lesson?.name ?? fallbackName
  if (!isLiveLesson(lesson) || !lesson?.starts_at) {
    return baseName
  }

  const lessonDate = formatDateV2(lesson.starts_at, { day: "2-digit", month: "2-digit", year: "numeric" })
  return `${baseName} (${lessonDate})`
}

const Materials = () => {
  const { t } = useTranslation()

  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [materialCourses, setMaterialCourses] = useState([])
  const [materials, setMaterials] = useState([])
  const [filteredMaterials, setFilteredMaterials] = useState([])
  const [modules, setModules] = useState([])
  const [loadingCourses, setLoadingCourses] = useState(false)
  const [loadingMaterialsByCourse, setLoadingMaterialsByCourse] = useState({})
  const [loadedCourseIds, setLoadedCourseIds] = useState([])
  const [loadingModules, setLoadingModules] = useState(false)
  const [loadingLessons, setLoadingLessons] = useState(false)
  const [waitingAdd, setWaitingAdd] = useState(false)
  const [waitingEdit, setWaitingEdit] = useState(false)
  const [materialsSearchQuery, setMaterialsSearchQuery] = useState("")

  const [lessons, setLessons] = useState([])
  const [selectedCourseId, setSelectedCourseId] = useState(null)
  const [selectedEditionId, setSelectedEditionId] = useState(null)
  const [selectedModuleId, setSelectedModuleId] = useState(null)
  const [selectedLessonId, setSelectedLessonId] = useState(null)
  const [files, setFiles] = useState([])
  const [disableAdd, setDisableAdd] = useState(true)
  const [uploaderReset, setUploaderReset] = useState(0)

  const [courseCollapsableState, setCourseCollapsableState] = useState([])
  const [selectedEditionByCourse, setSelectedEditionByCourse] = useState({})

  const [openDeleteAlert, setOpenDeleteAlert] = useState(false)
  const [deleteStatus, setDeleteStatus] = useState(DialogStatus.Default)
  const [selectedDeleteMaterial, setSelectedDeleteMaterial] = useState(null)

  const [editStatus, setEditStatus] = useState(DialogStatus.Default)
  const [selectedEditMaterial, setSelectedEditMaterial] = useState(null)
  const [selectedEditModuleId, setSelectedEditModuleId] = useState(null)
  const [selectedEditLessonId, setSelectedEditLessonId] = useState(null)

  const [courseSearch, setCourseSearch] = useState("")
  const [editionSearch, setEditionSearch] = useState("")
  const [moduleSearch, setModuleSearch] = useState("")
  const [lessonSearch, setLessonSearch] = useState("")
  const [editModuleSearch, setEditModuleSearch] = useState("")
  const [editLessonSearch, setEditLessonSearch] = useState("")

  const deleteCloseTimeoutRef = useRef(null)
  const editCloseTimeoutRef = useRef(null)

  const groupedMaterials = useMemo(() => groupMaterialsByCourseEdition(filteredMaterials, t), [filteredMaterials, t])
  const groupedMaterialsByCourse = useMemo(() => mapGroupsByCourseId(groupedMaterials), [groupedMaterials])

  const getMaterialCourses = useCallback(async () => {
    setLoadingCourses(true)
    try {
      const courses = await api.get("/teacher/materials/courses", { timeout: 0 })
      setMaterialCourses(courses ?? [])
    } catch (e) {
      console.error(e)
      setMaterialCourses([])
    }
    setLoadingCourses(false)
  }, [])

  useEffect(() => {
    const getModules = async () => {
      setLoadingModules(true)
      try {
        const modulesResponse = await api.get("/teacher/modules", { timeout: 0 })

        const modulesPerId = {}

        for (const moduleItem of modulesResponse ?? []) {
          const edition = moduleItem?.edition
          const course = edition?.course
          modulesPerId[moduleItem.id] = {
            id: moduleItem.id,
            label: moduleItem.name,
            courseId: course?.id,
            courseName: course?.name,
            editionId: edition?.id,
            editionName: edition?.name,
            subtitle: `${course?.name ?? ""} - ${edition?.name ?? ""}`,
            lessons: [...(moduleItem?.lessons ?? [])],
          }
        }

        setModules(Object.values(modulesPerId))
      } catch (e) {
        console.error(e)
        setModules([])
      }
      setLoadingModules(false)
    }

    getMaterialCourses()
    getModules()
  }, [getMaterialCourses])

  useEffect(() => {
    const query = materialsSearchQuery.toLowerCase().replaceAll(" ", "")
    if (!query) {
      setFilteredMaterials(materials)
      return
    }

    const nextFiltered = materials.filter((material) => {
      const name = `${material?.name ?? ""}`.toLowerCase().replaceAll(" ", "")
      const module = `${material?.module?.name ?? ""}`.toLowerCase().replaceAll(" ", "")
      const edition = `${material?.module?.edition?.name ?? ""}`.toLowerCase().replaceAll(" ", "")
      const course = `${material?.module?.course?.name ?? ""}`.toLowerCase().replaceAll(" ", "")
      const lesson = `${material?.lesson?.name ?? ""}`.toLowerCase().replaceAll(" ", "")
      return name.includes(query) || module.includes(query) || edition.includes(query) || course.includes(query) || lesson.includes(query)
    })
    setFilteredMaterials(nextFiltered)
  }, [materials, materialsSearchQuery])

  useEffect(() => {
    const courseIds = materialCourses.map((group) => group.id)
    setCourseCollapsableState((prev) => mergeCollapsedState(prev, courseIds, true))

    setSelectedEditionByCourse((prev) => {
      const next = {}

      for (const course of materialCourses) {
        const courseId = course.id
        const validEditionIds = (groupedMaterialsByCourse[courseId]?.editions ?? []).map((editionGroup) => editionGroup.edition.id)
        const previousSelection = prev[courseId]
        next[courseId] = validEditionIds.includes(previousSelection) ? previousSelection : null
      }

      return next
    })
  }, [groupedMaterialsByCourse, materialCourses])

  const courseOptions = useMemo(() => {
    const map = {}
    modules.forEach((moduleItem) => {
      if (!moduleItem.courseId) return
      if (!map[moduleItem.courseId]) {
        map[moduleItem.courseId] = {
          id: moduleItem.courseId,
          name: moduleItem.courseName,
        }
      }
    })
    return Object.values(map).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }))
  }, [modules])

  const editionOptions = useMemo(() => {
    if (!selectedCourseId) return []
    const map = {}

    modules.forEach((moduleItem) => {
      if (moduleItem.courseId !== selectedCourseId || !moduleItem.editionId) return
      if (!map[moduleItem.editionId]) {
        map[moduleItem.editionId] = {
          id: moduleItem.editionId,
          name: moduleItem.editionName,
        }
      }
    })

    return Object.values(map).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }))
  }, [modules, selectedCourseId])

  const moduleOptions = useMemo(() => {
    if (!selectedCourseId || !selectedEditionId) return []

    return modules
      .filter((moduleItem) => moduleItem.courseId === selectedCourseId && moduleItem.editionId === selectedEditionId)
      .map((moduleItem) => ({
        id: moduleItem.id,
        name: moduleItem.label,
        lessons: moduleItem.lessons,
        courseId: moduleItem.courseId,
        editionId: moduleItem.editionId,
      }))
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }))
  }, [modules, selectedCourseId, selectedEditionId])

  const filteredCourseOptions = useMemo(() => filterSelectOptions(courseOptions, courseSearch), [courseOptions, courseSearch])
  const filteredEditionOptions = useMemo(() => filterSelectOptions(editionOptions, editionSearch), [editionOptions, editionSearch])
  const filteredModuleOptions = useMemo(() => filterSelectOptions(moduleOptions, moduleSearch), [moduleOptions, moduleSearch])
  const filteredLessonOptions = useMemo(() => filterSelectOptions(lessons, lessonSearch), [lessons, lessonSearch])

  useEffect(() => {
    if (selectedCourseId === null && courseOptions.length === 1) {
      setSelectedCourseId(courseOptions[0].id)
    }
  }, [courseOptions, selectedCourseId])

  useEffect(() => {
    if (selectedCourseId === null) return

    const hasValidEdition = editionOptions.some((item) => item.id === selectedEditionId)

    if (!hasValidEdition) {
      if (editionOptions.length === 1) {
        setSelectedEditionId(editionOptions[0].id)
      } else if (selectedEditionId !== null) {
        setSelectedEditionId(null)
      }
    }
  }, [editionOptions, selectedCourseId, selectedEditionId])

  useEffect(() => {
    if (selectedCourseId === null || selectedEditionId === null) return

    const hasValidModule = moduleOptions.some((item) => item.id === selectedModuleId)

    if (!hasValidModule) {
      if (moduleOptions.length === 1) {
        setSelectedModuleId(moduleOptions[0].id)
      } else if (selectedModuleId !== null) {
        setSelectedModuleId(null)
      }
    }
  }, [moduleOptions, selectedCourseId, selectedEditionId, selectedModuleId])

  useEffect(() => {
    const selectedModuleData = moduleOptions.find((item) => item.id === selectedModuleId)

    const mapLessonsForSelect = (items = []) => {
      const nextLessons = [...items]
        .sort((a, b) => {
          const aTime = a?.starts_at ? new Date(a.starts_at).getTime() : 0
          const bTime = b?.starts_at ? new Date(b.starts_at).getTime() : 0
          return aTime - bTime
        })
        .map((lessonItem) => {
          const hasDateRange = lessonItem?.starts_at && lessonItem?.ends_at
          return {
            ...lessonItem,
            name: getLessonSelectName(lessonItem, lessonItem.name),
            subtitle: hasDateRange
              ? `${formatDateV2(lessonItem.starts_at, { day: "2-digit", month: "2-digit", year: "numeric" })}, ${formatTimeV2(lessonItem.starts_at)} - ${formatTimeV2(lessonItem.ends_at)}`
              : "",
          }
        })

      return [...nextLessons, { id: null, name: "Nessuna lezione" }]
    }

    if (!selectedModuleData) {
      setLessons([])
      setSelectedLessonId(null)
      setLoadingLessons(false)
      return
    }

    let isActive = true
    setLoadingLessons(true)

    api.get(`/teacher/modules/${selectedModuleData.id}/lessons`, { timeout: 0 })
      .then((moduleLessons) => {
        if (!isActive) return

        const resolvedLessons = Array.isArray(moduleLessons) && moduleLessons.length > 0
          ? moduleLessons
          : (selectedModuleData.lessons ?? [])

        setLessons(mapLessonsForSelect(resolvedLessons))
      })
      .catch((error) => {
        console.error(error)
        if (!isActive) return
        setLessons(mapLessonsForSelect(selectedModuleData.lessons ?? []))
      })
      .finally(() => {
        if (isActive) {
          setLoadingLessons(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [selectedModuleId, moduleOptions])

  useEffect(() => {
    setDisableAdd(selectedModuleId === null || files.length === 0 || waitingAdd)
  }, [selectedModuleId, files, waitingAdd])

  useEffect(() => {
    if (!showAddDialog) {
      setSelectedCourseId(null)
      setSelectedEditionId(null)
      setSelectedModuleId(null)
      setSelectedLessonId(null)
      setCourseSearch("")
      setEditionSearch("")
      setModuleSearch("")
      setLessonSearch("")
      setLessons([])
      setFiles([])
      setUploaderReset((prev) => prev + 1)
    }
  }, [showAddDialog])

  useEffect(() => {
    if (!showEditDialog) {
      setEditModuleSearch("")
      setEditLessonSearch("")
    }
  }, [showEditDialog])

  useEffect(() => {
    return () => {
      if (deleteCloseTimeoutRef.current) {
        clearTimeout(deleteCloseTimeoutRef.current)
      }
      if (editCloseTimeoutRef.current) {
        clearTimeout(editCloseTimeoutRef.current)
      }
    }
  }, [])

  const editModuleOptions = useMemo(() => {
    return [...modules]
      .map((moduleItem) => ({ ...moduleItem, name: moduleItem.label }))
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }))
  }, [modules])

  const editLessons = useMemo(() => {
    const moduleData = editModuleOptions.find((moduleItem) => moduleItem.id === selectedEditModuleId)
    if (!moduleData) return []

    const mappedLessons = [...(moduleData.lessons ?? [])]
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))
      .map((lessonItem) => ({
        ...lessonItem,
        name: getLessonSelectName(lessonItem, lessonItem.name),
        subtitle: `${formatDateV2(lessonItem.starts_at, { day: "2-digit", month: "2-digit", year: "numeric" })}, ${formatTimeV2(lessonItem.starts_at)} - ${formatTimeV2(lessonItem.ends_at)}`,
      }))

    mappedLessons.push({ id: null, name: "Nessuna lezione" })
    return mappedLessons
  }, [editModuleOptions, selectedEditModuleId])

  const filteredEditModuleOptions = useMemo(() => filterSelectOptions(editModuleOptions, editModuleSearch), [editModuleOptions, editModuleSearch])
  const filteredEditLessonOptions = useMemo(() => filterSelectOptions(editLessons, editLessonSearch), [editLessons, editLessonSearch])

  const onSearch = useCallback((text) => {
    setMaterialsSearchQuery(text ?? "")
  }, [])

  const fetchMaterialsByCourse = useCallback(async (courseId) => {
    if (!courseId || loadingMaterialsByCourse[courseId]) {
      return
    }

    setLoadingMaterialsByCourse((prev) => ({ ...prev, [courseId]: true }))
    try {
      const nextMaterials = await api.get(`/teacher/materials?course_id=${courseId}`, { timeout: 0 })
      setMaterials((prev) => {
        const withoutCourse = prev.filter((item) => item?.module?.course?.id !== courseId)
        return [...withoutCourse, ...(nextMaterials ?? [])]
      })
      setLoadedCourseIds((prev) => (prev.includes(courseId) ? prev : [...prev, courseId]))
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingMaterialsByCourse((prev) => ({ ...prev, [courseId]: false }))
    }
  }, [loadingMaterialsByCourse])

  const onToggleCourse = useCallback((courseId) => {
    setCourseCollapsableState((prev) => {
      const courseItem = prev.find((item) => item.id === courseId)
      const nextCollapsed = courseItem ? !courseItem.collapsed : false

      if (courseItem && courseItem.collapsed && !loadedCourseIds.includes(courseId)) {
        fetchMaterialsByCourse(courseId)
      }

      return prev.map((item) => (
        item.id === courseId ? { ...item, collapsed: nextCollapsed } : item
      ))
    })
  }, [fetchMaterialsByCourse, loadedCourseIds])

  const addMaterial = useCallback(() => {
    setWaitingAdd(true)

    const payload = {
      module_id: selectedModuleId,
      files,
    }

    if (selectedLessonId !== null) {
      payload.lesson_id = selectedLessonId
    }

    api.post("teacher/materials", payload, { timeout: 0 })
      .then((response) => {
        const { materials: newMaterials } = response
        setShowAddDialog(false)
        setMaterials((prev) => [...prev, ...(newMaterials ?? [])])
        getMaterialCourses()
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        setWaitingAdd(false)
      })
  }, [files, getMaterialCourses, selectedLessonId, selectedModuleId])

  const onDelete = useCallback((materialId) => {
    setMaterials((prev) => prev.filter((material) => material.id !== materialId))
  }, [])

  const onEdit = useCallback((material) => {
    setMaterials((prev) => {
      const withoutMaterial = prev.filter((item) => item.id !== material.id)
      return [...withoutMaterial, material]
    })
  }, [])

  const deleteMaterial = useCallback(async () => {
    if (!selectedDeleteMaterial) return

    try {
      setDeleteStatus(DialogStatus.Loading)
      await api.delete(`/teacher/materials/${selectedDeleteMaterial.id}`, { timeout: 0 })
      onDelete(selectedDeleteMaterial.id)
      getMaterialCourses()
      setDeleteStatus(DialogStatus.Success)
      deleteCloseTimeoutRef.current = setTimeout(() => {
        setOpenDeleteAlert(false)
        setDeleteStatus(DialogStatus.Default)
        setSelectedDeleteMaterial(null)
      }, 1200)
    } catch (e) {
      console.error(e)
      setDeleteStatus(DialogStatus.Error)
    }
  }, [getMaterialCourses, onDelete, selectedDeleteMaterial])

  const editMaterial = useCallback(async () => {
    if (!selectedEditMaterial || !selectedEditModuleId) return

    try {
      setWaitingEdit(true)
      setEditStatus(DialogStatus.Loading)

      const body = {
        module_id: selectedEditModuleId,
        lesson_id: selectedEditLessonId,
      }

      const updatedMaterial = await api.put(`/teacher/materials/${selectedEditMaterial.id}`, body, { timeout: 0 })
      const selectedModule = modules.find((moduleItem) => moduleItem.id === selectedEditModuleId)
      const selectedLessonObject = selectedEditLessonId === null
        ? null
        : selectedModule?.lessons?.find((lessonItem) => lessonItem.id === selectedEditLessonId) ?? null

      onEdit({
        ...updatedMaterial,
        module: selectedModule ?? updatedMaterial.module,
        lesson: selectedLessonObject,
      })
      getMaterialCourses()

      setEditStatus(DialogStatus.Success)
      editCloseTimeoutRef.current = setTimeout(() => {
        setShowEditDialog(false)
      }, 1200)
    } catch (e) {
      console.error(e)
      setEditStatus(DialogStatus.Error)
    } finally {
      setWaitingEdit(false)
    }
  }, [getMaterialCourses, modules, onEdit, selectedEditLessonId, selectedEditMaterial, selectedEditModuleId])

  const downloadMaterial = useCallback((material) => {
    const anchor = document.createElement("a")
    anchor.href = material.url
    anchor.download = material.name
    anchor.click()
  }, [])

  const getMenuActions = useCallback((material) => ([
    {
      label: t("download"),
      icon: DownloadIcon,
      style: { color: "var(--sf-green)" },
      onClick: () => downloadMaterial(material),
    },
    {
      label: t("edit"),
      icon: EditIcon,
      style: { color: "var(--primary)" },
      onClick: () => {
        setSelectedEditMaterial(material)
        setSelectedEditModuleId(material?.module?.id ?? null)
        setSelectedEditLessonId(material?.lesson?.id ?? null)
        setEditStatus(DialogStatus.Default)
        setShowEditDialog(true)
      },
    },
    {
      label: t("delete"),
      icon: DeleteIcon,
      style: { color: "var(--secondary)" },
      onClick: () => {
        setSelectedDeleteMaterial(material)
        setDeleteStatus(DialogStatus.Default)
        setOpenDeleteAlert(true)
      },
    },
  ]), [downloadMaterial, t])

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>Materiali didattici</title>
        </Helmet>
      </HelmetProvider>

      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <div className={styles.content}>
              <div className={styles.header}>
                <div className={typo.title}>{t("materials.title")}</div>

                <div className={styles.add}>
                  <Button
                    appearance="subtle"
                    disabled={loadingCourses}
                    onClick={() => setShowAddDialog(true)}
                  >
                    <AddIcon />
                    {t("materials.add").toUpperCase()}
                  </Button>
                </div>
              </div>

              <TextInput
                autocomplete={false}
                onKeyUp={onSearch}
                type="search"
                placeholder={"Ricerca"}
                style={{ backgroundColor: "var(--background-secondary-color)" }}
              />

              <div className={styles.modules}>
                {
                  loadingCourses === true &&
                  <Each
                    of={[1, 2]}
                    render={() => (
                      <Card>
                        <div className={styles.courseSkeleton}>
                          <div className={styles.courseSkeletonHeader}>
                            <Skeleton type="rect" width="256px" height={"22px"} borderRadius={"8px"} />
                          </div>
                        </div>
                      </Card>
                    )}
                  />
                }

                {
                  loadingCourses === false && materialCourses.length > 0 &&
                  <Each
                    of={materialCourses}
                    render={(course) => {
                      const courseGroup = groupedMaterialsByCourse[course.id]
                      const isCourseCollapsed = courseCollapsableState.find((item) => item.id === course.id)?.collapsed
                      const selectedEditionIdForCourse = selectedEditionByCourse[course.id] ?? null
                      const isCourseLoading = loadingMaterialsByCourse[course.id] === true
                      const selectedEditionGroup = selectedEditionIdForCourse
                        ? (courseGroup?.editions ?? []).find((editionGroup) => editionGroup.edition.id === selectedEditionIdForCourse)
                        : null
                      const materialsToShow = selectedEditionGroup
                        ? selectedEditionGroup.materials
                        : (courseGroup?.editions ?? []).flatMap((editionGroup) => editionGroup.materials)

                      return (
                        <Card
                          title={
                            <div
                              className={styles.courseHeader}
                              onClick={() => {
                                onToggleCourse(course.id)
                              }}
                            >
                              {
                                course.thumbnail
                                  ? <img src={course.thumbnail} alt="" className={styles.courseThumbnail} />
                                  : <div className={styles.courseThumbnailFallback}>{course.name?.charAt(0) ?? "-"}</div>
                              }
                              <div className={styles.courseTitle}>{course.name}</div>
                            </div>
                          }
                        >
                          <ChevronIcon
                            className={`${styles.courseChevron} ${isCourseCollapsed ? styles.collapsed : ""}`}
                            onClick={() => {
                              onToggleCourse(course.id)
                            }}
                          />

                          <div className={`${styles.courseBody} ${isCourseCollapsed ? styles.collapsed : ""}`}>
                            {isCourseLoading &&
                              <div className={styles.courseMaterialsLoader}>
                                <Loader size={24} />
                              </div>
                            }

                            {!isCourseLoading && courseGroup &&
                              <>
                                <div className={styles.editionFilters}>
                                  <button
                                    className={`${styles.editionFilter} ${selectedEditionIdForCourse === null ? styles.selected : ""}`}
                                    onClick={() => {
                                      setSelectedEditionByCourse((prev) => ({ ...prev, [course.id]: null }))
                                    }}
                                  >
                                    Tutto
                                  </button>

                                  <Each
                                    of={courseGroup.editions}
                                    render={(editionGroup) => (
                                      <button
                                        className={`${styles.editionFilter} ${selectedEditionIdForCourse === editionGroup.edition.id ? styles.selected : ""}`}
                                        onClick={() => {
                                          setSelectedEditionByCourse((prev) => ({ ...prev, [course.id]: editionGroup.edition.id }))
                                        }}
                                      >
                                        {editionGroup.edition.name}
                                      </button>
                                    )}
                                  />
                                </div>

                                <Table
                                  appearance="minimal"
                                  columns={[
                                    { key: "name", label: t("materials.title"), type: "custom" },
                                    { key: "file_type", label: "TIPO", type: "custom" },
                                    { key: "module_name", label: "MODULO", type: "custom" },
                                    { key: "lesson_name", label: "LEZIONE", type: "custom" },
                                    { key: "size", label: "DIMENSIONE", type: "custom" },
                                    { key: "created_at", label: "CREATO IL", type: "datetime" },
                                    { key: "_actions", label: "AZIONI", type: "custom" },
                                  ]}
                                  elements={materialsToShow.map((material) => ({
                                    ...material,
                                    file_type: {
                                      value: getMaterialFileType(material),
                                      type: "string",
                                      component: <div className={styles.fileTypeLabel}>{getMaterialFileType(material)}</div>,
                                    },
                                    name: {
                                      value: material.name ?? "-",
                                      type: "string",
                                      component: <div className={styles.materialNameClamp}>{material.name ?? "-"}</div>,
                                    },
                                    module_name: {
                                      value: material.module?.name ?? "-",
                                      type: "string",
                                      component: <div className={styles.clampedLabel}>{material.module?.name ?? "-"}</div>,
                                    },
                                    lesson_name: {
                                      value: material.lesson?.name ?? "Nessuna",
                                      type: "string",
                                      component: <div className={styles.clampedLabel}>{material.lesson?.name ?? "Nessuna"}</div>,
                                    },
                                    size: {
                                      value: material.size ?? 0,
                                      type: "number",
                                      component: formatFileSize(material.size ?? 0),
                                    },
                                    _actions: {
                                      value: material.id,
                                      type: "number",
                                      component: (
                                        <div className={styles.actionCell}>
                                          <MeatBallsMenu disabled={loadingModules} actions={getMenuActions(material)} />
                                        </div>
                                      ),
                                    },
                                  }))}
                                />
                              </>
                            }

                            {!isCourseLoading && !courseGroup && loadedCourseIds.includes(course.id) &&
                              <div className={styles.emptyCourseMaterials}>
                                Nessun materiale disponibile per questo corso.
                              </div>
                            }
                          </div>
                        </Card>
                      )
                    }}
                  />
                }

                {
                  loadingCourses === false && materialCourses.length === 0 &&
                  <EmptyContent
                    Illustration={EmptyMaterials}
                    message="Non ci sono ancora materiali didattici, aggiungine uno per cominciare!"
                    imageStyle={{ width: "192px" }}
                  />
                }
              </div>

              <Dialog
                open={showAddDialog}
                title={t("materials.addFile")}
                style={{ maxWidth: "512px" }}
                onClose={() => setShowAddDialog(false)}
                action={
                  <Button onClick={addMaterial} disabled={disableAdd}>
                    {t("materials.add").toUpperCase()}
                  </Button>
                }
              >
                <div className={styles.dialog}>
                  <Select
                    disabled={courseOptions.length === 0 || waitingAdd}
                    placeholder={"Associa un corso"}
                    selected={courseOptions.find((item) => item.id === selectedCourseId) ?? null}
                    options={filteredCourseOptions}
                    onSearch={(value) => setCourseSearch(value ?? "")}
                    searchPlaceholder={"Cerca corso"}
                    onSelect={(item) => {
                      setSelectedCourseId(item?.id ?? null)
                      setSelectedEditionId(null)
                      setSelectedModuleId(null)
                      setSelectedLessonId(null)
                      setEditionSearch("")
                      setModuleSearch("")
                      setLessonSearch("")
                    }}
                  />

                  <Select
                    disabled={selectedCourseId === null || editionOptions.length === 0 || waitingAdd}
                    placeholder={"Associa un'edizione"}
                    selected={editionOptions.find((item) => item.id === selectedEditionId) ?? null}
                    options={filteredEditionOptions}
                    onSearch={(value) => setEditionSearch(value ?? "")}
                    searchPlaceholder={"Cerca edizione"}
                    onSelect={(item) => {
                      setSelectedEditionId(item?.id ?? null)
                      setSelectedModuleId(null)
                      setSelectedLessonId(null)
                      setModuleSearch("")
                      setLessonSearch("")
                    }}
                  />

                  <Select
                    disabled={selectedEditionId === null || moduleOptions.length === 0 || waitingAdd}
                    placeholder={"Associa un modulo"}
                    selected={moduleOptions.find((item) => item.id === selectedModuleId) ?? null}
                    options={filteredModuleOptions}
                    onSearch={(value) => setModuleSearch(value ?? "")}
                    searchPlaceholder={"Cerca modulo"}
                    onSelect={(item) => {
                      setSelectedModuleId(item?.id ?? null)
                      setSelectedLessonId(null)
                      setLessonSearch("")
                    }}
                  />

                  <Select
                    disabled={selectedModuleId === null || waitingAdd}
                    loading={loadingLessons}
                    placeholder={"Associa una lezione"}
                    selected={lessons.find((item) => item.id === selectedLessonId) ?? null}
                    options={filteredLessonOptions}
                    onSearch={(value) => setLessonSearch(value ?? "")}
                    searchPlaceholder={"Cerca lezione"}
                    onSelect={(item) => setSelectedLessonId(item?.id ?? null)}
                  />

                  <div className={styles.dropzoneContainer}>
                    <div className={styles.subtitle}>Materiali</div>
                    <Uploader
                      add_timestamp={true}
                      multiple={true}
                      reset={uploaderReset}
                      onProgress={(_, uploads) => {
                        setFiles(
                          (uploads || [])
                            .filter((upload) => Boolean(upload?.url))
                            .map((upload) => ({
                              url: upload.url,
                              name: upload?.file?.name || "material",
                              size: upload?.file?.size || 0,
                            }))
                        )
                      }}
                      onComplete={(uploads) => {
                        setFiles(
                          (uploads || [])
                            .filter((upload) => Boolean(upload?.url))
                            .map((upload) => ({
                              url: upload.url,
                              name: upload?.file?.name || "material",
                              size: upload?.file?.size || 0,
                            }))
                        )
                      }}
                      onDelete={(uploads) => {
                        setFiles(
                          (uploads || [])
                            .filter((upload) => Boolean(upload?.url))
                            .map((upload) => ({
                              url: upload.url,
                              name: upload?.file?.name || "material",
                              size: upload?.file?.size || 0,
                            }))
                        )
                      }}
                    />
                    {
                      waitingAdd === true &&
                      <div className={styles.loader}>
                        <Loader />
                      </div>
                    }
                  </div>
                </div>
              </Dialog>

              <Dialog
                overflow="visible"
                open={showEditDialog}
                title={t("materials.editTitle")}
                style={{ maxWidth: "512px" }}
                status={editStatus}
                onClose={() => {
                  setShowEditDialog(false)
                  setSelectedEditMaterial(null)
                  setSelectedEditModuleId(null)
                  setSelectedEditLessonId(null)
                  setEditStatus(DialogStatus.Default)
                  if (editCloseTimeoutRef.current) {
                    clearTimeout(editCloseTimeoutRef.current)
                  }
                }}
                action={
                  <Button onClick={editMaterial} disabled={!selectedEditModuleId || waitingEdit || editStatus !== DialogStatus.Default}>
                    {t("materials.save").toUpperCase()}
                  </Button>
                }
              >
                <div className={styles.dialog}>
                  <Select
                    disabled={editModuleOptions.length === 0 || waitingEdit}
                    placeholder={"Associa un modulo"}
                    selected={editModuleOptions.find((moduleItem) => moduleItem.id === selectedEditModuleId) ?? null}
                    options={filteredEditModuleOptions}
                    onSearch={(value) => setEditModuleSearch(value ?? "")}
                    searchPlaceholder={"Cerca modulo"}
                    onSelect={(item) => {
                      setSelectedEditModuleId(item?.id ?? null)
                      setSelectedEditLessonId(null)
                      setEditLessonSearch("")
                    }}
                  />

                  <Select
                    disabled={selectedEditModuleId === null || editLessons.length === 0 || waitingEdit}
                    placeholder={"Associa una lezione"}
                    selected={editLessons.find((lessonItem) => lessonItem.id === selectedEditLessonId) ?? null}
                    options={filteredEditLessonOptions}
                    onSearch={(value) => setEditLessonSearch(value ?? "")}
                    searchPlaceholder={"Cerca lezione"}
                    onSelect={(item) => {
                      setSelectedEditLessonId(item?.id ?? null)
                    }}
                  />
                </div>
              </Dialog>

              <AlertDialog
                open={openDeleteAlert}
                title={t("materials.deleteTitle")}
                text={t("materials.deleteText")}
                status={deleteStatus}
                onClose={() => {
                  setOpenDeleteAlert(false)
                  setDeleteStatus(DialogStatus.Default)
                  setSelectedDeleteMaterial(null)
                  if (deleteCloseTimeoutRef.current) {
                    clearTimeout(deleteCloseTimeoutRef.current)
                  }
                }}
                actions={[
                  {
                    label: t("materials.deleteConfirm").toUpperCase(),
                    onClick: deleteMaterial,
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </HeaderFooterLayout>
  )
}

export default Materials
